import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryWithSimpleErrorHandling } from 'hooks';
import { LEGAL_PARTNERS_QUERY, INDIVIDUAL_PARTNERS_QUERY, CITIES_QUERY } from '../queries';
import { NATIONALITIES_QUERY } from 'features/insuranceDocument/edit/general/partner/queries';
import SearchPartnerComponent from './SearchPartnerComponent';
import { defaultPager } from 'constants/defaultValues';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import usePagination from 'features/common/hooks/usePagination';

const SearchPartnerContainer = ({ isPF, partnerTypeId, counties, countiesLoading, onCloseModal, onCopyPartner }) => {
  const [legalPartners, setLegalPartners] = useState([]);
  const [individualPartners, setIndividualPartners] = useState([]);
  const [selected, setSelected] = useState(false);
  const { Pagination, pager, updatePager } = usePagination({ rowsPerPageOptions: [5, 10, 25, 50, 100], pageSize: 5 });
  const emptyFilter = {
    PJ: {
      uniqueId: '',
      name: ''
    },
    PF: {
      fullName: '',
      citizenshipId: null,
      idSeriesAndNumber: '',
      birthDate: null,
      countyId: null,
      cityId: null
    }
  };

  const [filterLens] = useChangeTrackingLens({ ...emptyFilter });

  const { loading: loadingLegalPartners, refetch: refetchPJ } = useQueryWithSimpleErrorHandling(LEGAL_PARTNERS_QUERY, {
    variables: {
      page: pager.page + 1,
      pageSize: pager.pageSize,
      uniqueId: filterLens.PJ.uniqueId |> get,
      name: filterLens.PJ.name |> get,
      partnerTypeId: partnerTypeId
    },
    skip: isPF(partnerTypeId),
    onCompleted: data => {
      const values = data?.legalPartners?.values.map(z => ({
        partnerId: z.partnerId,
        partnerTypeId: z.partnerTypeId,
        uniqueId: z.uniqueId,
        name: z.name,
        legalForm: z.legalForm
      }));
      setLegalPartners(values);
      updatePager({
        page: (data?.legalPartners?.page ? data?.legalPartners?.page - 1 : null) || defaultPager.page,
        pageSize: data?.legalPartners?.pageSize || defaultPager.pageSize,
        totalCount: data?.legalPartners?.totalCount || defaultPager.totalCount,
        totalPages: data?.legalPartners?.totalPages || defaultPager.totalPages
      });
    },
    fetchPolicy: 'network-only'
  });

  const { loading: loadingIndividualPartners, refetch: refetchPF } = useQueryWithSimpleErrorHandling(INDIVIDUAL_PARTNERS_QUERY, {
    variables: {
      page: pager.page + 1,
      pageSize: pager.pageSize,
      fullName: filterLens.PF.fullName |> get,
      citizenshipId: filterLens.PF.citizenshipId |> get,
      birthDate: filterLens.PF.birthDate |> get,
      countyId: filterLens.PF.countyId |> get,
      cityId: filterLens.PF.cityId |> get,
      idSeriesAndNumber: filterLens.PF.idSeriesAndNumber |> get
    },
    skip: !isPF(partnerTypeId),
    onCompleted: data => {
      const values = data?.individualPartners?.values.map(z => ({
        partnerId: z.partnerId,
        partnerTypeId: z.partnerTypeId,
        uniqueId: z.uniqueId,
        fullName: z.fullName,
        citizenship: z.citizenship,
        nationality: z.nationality,
        idSeriesAndNumber: z.idSeriesAndNumber,
        birthDate: z.birthDate,
        country: z.country,
        county: z.county,
        city: z.city
      }));
      setIndividualPartners(values);
      updatePager({
        page: (data?.individualPartners?.page ? data?.individualPartners?.page - 1 : null) || defaultPager.page,
        pageSize: data?.individualPartners?.pageSize || defaultPager.pageSize,
        totalCount: data?.individualPartners?.totalCount || defaultPager.totalCount,
        totalPages: data?.individualPartners?.totalPages || defaultPager.totalPages
      });
    },
    fetchPolicy: 'network-only'
  });

  const countyId = filterLens.PF.countyId |> get;
  const { loading: citiesLoading, data: citiesData } = useQueryWithSimpleErrorHandling(CITIES_QUERY, {
    variables: { countyId: countyId },
    skip: !countyId
  });

  const { loading: citizenshipsLoading, data: citizenshipsData } = useQueryWithSimpleErrorHandling(NATIONALITIES_QUERY);

  const handleCountyChange = useCallback(
    value => {
      (filterLens.PF.countyId |> set)(value);
      (filterLens.PF.cityId |> set)(null);
    },
    [filterLens]
  );

  const handleToggleSelectItem = useCallback(
    partnerId => {
      const partners = isPF(partnerTypeId) ? individualPartners : legalPartners;
      for (let i = 0; i < partners.length; i++) {
        if (partners[i].partnerId === partnerId) {
          partners[i].selected = !partners[i].selected;
          setSelected(partners[i]);
        } else {
          partners[i].selected = false;
        }
      }
    },
    [individualPartners, isPF, legalPartners, partnerTypeId]
  );

  const handleCopyPartner = useCallback(() => {
    const partners = isPF(partnerTypeId) ? individualPartners : legalPartners;
    onCopyPartner(partners);
  }, [individualPartners, isPF, legalPartners, onCopyPartner, partnerTypeId]);

  const handleRefresh = useCallback(() => {
    isPF(partnerTypeId) ? refetchPF() : refetchPJ();
  }, [isPF, partnerTypeId, refetchPF, refetchPJ]);

  const handleChangePage = useCallback(
    newPage => {
      if (loadingLegalPartners || loadingIndividualPartners) return;
      updatePager({ ...pager, page: newPage });
    },
    [loadingIndividualPartners, loadingLegalPartners, pager, updatePager]
  );

  const handleChangeRowsPerPage = useCallback(
    pageSize => updatePager({ ...pager, pageSize: parseInt(pageSize, 10) }),
    [pager, updatePager]
  );

  useEffect(() => {
    if (filterLens) updatePager({ ...defaultPager, pageSize: pager.pageSize });
  }, [filterLens, updatePager, pager.pageSize]);

  return (
    <SearchPartnerComponent
      partners={isPF(partnerTypeId) ? individualPartners : legalPartners}
      filterLens={filterLens}
      isPF={isPF(partnerTypeId)}
      citizenships={citizenshipsData?.nationalities}
      citizenshipsLoading={citizenshipsLoading}
      counties={counties}
      countiesLoading={countiesLoading}
      cities={citiesData?.cities}
      citiesLoading={citiesLoading}
      selected={selected}
      onCountyChange={handleCountyChange}
      onToggleSelectItem={handleToggleSelectItem}
      onCloseModal={onCloseModal}
      onCopyPartner={handleCopyPartner}
      pager={pager}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      onRefresh={handleRefresh}
      Pagination={Pagination}
    />
  );
};

SearchPartnerContainer.propTypes = {
  isPF: PropTypes.func.isRequired,
  partnerTypeId: PropTypes.number.isRequired,
  counties: PropTypes.array.isRequired,
  countiesLoading: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onCopyPartner: PropTypes.func.isRequired
};

export default SearchPartnerContainer;
